import { Button } from "antd";
import { I18n } from "@assets/strings";
import React, { useState } from "react";
import styled from "styled-components";
import { config } from "@core/config/config";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { useIsMobile } from "@utils/hooks";
import { isEmail } from "@utils/regex";
import { SecondaryButton } from "@ui/components/buttons/secondaryButton";
import { PlusIcon } from "@ui/components/icons";
import { TagsInput } from "@ui/components/inputs/tagsInput/tagsInput";
import { Row } from "@ui/components/layout";
import { ShareOnSocialMedia } from "@ui/components/shareOnSocialMedia";
import { BottomSheetModal } from "@ui/modals/bottomSheetModal";
import { ClosableModal } from "@ui/modals/closableModal";
import { useModalTools } from "@ui/modals/modalContext";
import { createToast } from "@ui/toasts/utils";

type InvitationButtonProps = React.HTMLAttributes<HTMLButtonElement>;
export const InvitationButton: React.FC<InvitationButtonProps> = ({ ...htmlProps }) => {
	const [opened, setOpened] = useState<boolean>(false);
	const isMobile = useIsMobile();
	const { createModal } = useModalTools();

	return (
		<>
			<SecondaryButton
				lighter
				style={{ marginLeft: 20 }}
				{...htmlProps}
				onClick={() => {
					if (isMobile) setOpened(true);
					else
						createModal((close) => (
							<ClosableModal close={close}>
								<ContentModal setOpened={close} />
							</ClosableModal>
						));
				}}
			>
				<PlusIcon width={18} height={18} style={{ marginRight: 5 }} /> {I18n.t("profile.card.invite.title")}
			</SecondaryButton>
			{isMobile && opened && (
				<BottomSheetModal reportClosed={() => setOpened(false)} manualOpen={opened}>
					<ContentModal setOpened={() => setOpened(false)} />
				</BottomSheetModal>
			)}
		</>
	);
};

interface ContentModalProps extends React.HTMLAttributes<HTMLDivElement> {
	setOpened: () => void;
}

const ContentModal: React.FC<ContentModalProps> = ({ setOpened, ...props }) => {
	const [emails, setEmails] = useState<string[]>([]);
	const { notificationService } = useServices();
	const currentProfile = useCurrentProfile();

	return currentProfile ? (
		<div style={{ width: "-webkit-fill-available" }} {...props}>
			<StyledTitleRow>{I18n.t("profile.card.invite.title")}</StyledTitleRow>
			<StyledInputRow>
				<TagsInput
					suggestionsDisabled
					placeHolderFirstWord="Emails"
					tags={emails}
					setTags={(newEmails: string[]) => {
						setEmails([...newEmails]);
					}}
					forbidDupplicates
					validator={(value) => isEmail(value.trim())}
				/>
			</StyledInputRow>
			<ShareBloc>
				<StyledInviteButton
					onClick={async () => {
						// ToDo: Implement Call With Toasts
						try {
							await notificationService.inviteFriends(emails);
							createToast(I18n.t("success"), I18n.t("success"), "success");
							setOpened();
						} catch {
							createToast(I18n.t("error"), I18n.t("error"), "error");
							setOpened();
						}
					}}
					disabled={!emails || emails.length === 0}
				>
					{I18n.t("profile.card.invite.action.send_invitation_button_label")}
				</StyledInviteButton>

				{config.features.facebookSharing && <ShareOnSocialMedia message={I18n.t("profile.card.invite.message")} />}
			</ShareBloc>
		</div>
	) : null;
};

const ShareBloc = styled(Row)`
	margin-bottom: 20;
	display: flex;
	align-items: center;
	justify-content: flex-start;
	gap: 10px;
`;

const StyledInviteButton = styled(Button)`
	width: 100%;
	padding: 10px 0px;
	background-color: ${({ theme }) => theme.colors.primary};
	font-family: Sora;
	font-size: 16px;
	border: none;
	border-radius: 8px;
	color: white;
	cursor: pointer;

	:disabled {
		background-color: ${({ theme }) => theme.colors.lightgray};
		cursor: not-allowed;
	}
`;

const StyledTitleRow = styled(Row)`
	padding-top: 0;
	margin-bottom: 20px;
	font-family: Sora;
	color: #13131a;
	font-size: 0.9375rem;
	font-weight: 700;
	margin-top: 0;
	font-size: 1.25rem;
`;

const StyledInputRow = styled(Row)`
	margin-bottom: 20px;
`;
